/**
 * Фронтенд-сервис электронных весов.
 *
 * Android использует native ScalesPlugin (COM/USB serial), desktop —
 * локальный Hardware Agent по WebSocket. Отсутствие весов не блокирует кассу:
 * остаётся ручной ввод веса.
 */

import {
  isNativeScales,
  nativeScalesOnStatus,
  nativeScalesOnWeight,
  nativeScalesStart,
  nativeScalesStatus,
  type NativeScaleReading,
  type NativeScaleStatus
} from '@core/native/scales'
import { debugLog } from '@core/utils/log'

export interface ScaleStatus {
  /** Весы подключены и готовы отдавать вес. */
  connected: boolean
  /** Порт весов (COM3 / /dev/ttyS1 / /dev/ttyUSB0), если serial-режим. */
  port?: string
  /** Протокол: atol-marta | mer328 | mer328-cas-m | mer328-cas-m-passive | mer328-pos2m | cas | massak | mtsics | digi | bizerba | generic. */
  protocol?: string
  /** Режим: native | serial | emulator. */
  mode?: string
  /** Скорость порта для native-режима. */
  baudRate?: number
  /** Native-контроллер ещё ищет устройство. */
  starting?: boolean
  /** Последний сырой кадр для диагностики. */
  raw?: string
  /** Последний сырой кадр в hex (бинарные POS2-кадры). */
  rawHex?: string
  /** Время последнего сырого кадра. */
  lastRawAt?: number
  /** Причина, если подключиться не удалось. */
  error?: string
}

/** Показание весов, нормализованное к килограммам. */
export interface ScaleWeightReading {
  kg: number
  stable: boolean
  raw?: string
}

type WeightListener = (reading: ScaleWeightReading) => void
type StatusListener = (status: ScaleStatus) => void

export class ScalesService {
  private ws: WebSocket | null = null
  private agentConnected = false
  private device: ScaleStatus = { connected: false }
  private readonly weightListeners = new Set<WeightListener>()
  private readonly statusListeners = new Set<StatusListener>()
  private nativeStarted = false
  private nativeListenersAttached = false

  /** Подключиться к источнику весов (native Android или Hardware Agent). */
  public connect(url = "ws://127.0.0.1:8089"): void {
    if (isNativeScales()) {
      void this.connectNative()
      return
    }
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return
    }

    try {
      this.ws = new WebSocket(url)

      this.ws.onopen = () => {
        this.agentConnected = true
        debugLog("[ScalesService] Connected to Local Hardware Agent")
        this.requestStatus()
      }

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(String(event.data))
          if (msg.event === "SCALE_STATUS") {
            this.device = {
              connected: Boolean(msg.connected),
              port: msg.port,
              protocol: msg.protocol,
              mode: msg.mode,
              raw: msg.raw,
              rawHex: msg.rawHex,
              error: msg.error
            }
            this.notifyStatus()
          } else if (msg.event === "SCALE_WEIGHT") {
            const kg = Number(msg.kg)
            if (!Number.isFinite(kg)) return
            const reading: ScaleWeightReading = {
              kg,
              stable: Boolean(msg.stable),
              raw: typeof msg.raw === "string" ? msg.raw : undefined
            }
            for (const listener of this.weightListeners) listener(reading)
          }
        } catch (err) {
          console.error("[ScalesService] Failed to parse agent message:", err)
        }
      }

      this.ws.onclose = () => {
        this.agentConnected = false
        this.device = { connected: false }
        this.notifyStatus()
        setTimeout(() => this.connect(url), 5000)
      }

      this.ws.onerror = () => {
        this.agentConnected = false
      }
    } catch {
      this.agentConnected = false
    }
  }

  private async connectNative(): Promise<void> {
    if (this.nativeStarted) {
      this.requestStatus()
      return
    }
    this.nativeStarted = true
    this.device = { connected: false, mode: "native" }
    try {
      await this.attachNativeListeners()
      this.applyNativeStatus(await nativeScalesStart())
    } catch (error) {
      // Если bridge/plugin не поднялся, разрешаем повторный connect().
      this.nativeStarted = false
      this.device = {
        connected: false,
        mode: "native",
        error: error instanceof Error ? error.message : String(error)
      }
      this.notifyStatus()
    }
  }

  private async attachNativeListeners(): Promise<void> {
    if (this.nativeListenersAttached) return
    this.nativeListenersAttached = true
    try {
      await nativeScalesOnStatus((status) => this.applyNativeStatus(status))
      await nativeScalesOnWeight((reading) => this.applyNativeReading(reading))
    } catch (error) {
      this.nativeListenersAttached = false
      throw error
    }
  }

  private applyNativeStatus(status: NativeScaleStatus): void {
    this.device = {
      connected: Boolean(status.connected),
      port: status.port,
      protocol: status.protocol,
      mode: "native",
      baudRate: status.baudRate,
      starting: status.starting,
      raw: status.raw,
      rawHex: status.rawHex,
      lastRawAt: status.lastRawAt,
      error: status.error
    }
    this.notifyStatus()
  }

  private applyNativeReading(reading: NativeScaleReading): void {
    const kg = Number(reading.kg)
    if (!Number.isFinite(kg)) return
    const normalized: ScaleWeightReading = {
      kg,
      stable: Boolean(reading.stable),
      raw: typeof reading.raw === "string" ? reading.raw : undefined
    }
    for (const listener of this.weightListeners) listener(normalized)
  }

  /** Подписка на показания весов; возвращает функцию отписки. */
  public onWeight(cb: WeightListener): () => void {
    this.weightListeners.add(cb)
    return () => this.weightListeners.delete(cb)
  }

  /** Подписка на статус весов (сразу получает текущее значение). */
  public onStatus(cb: StatusListener): () => void {
    this.statusListeners.add(cb)
    cb(this.getStatus())
    return () => this.statusListeners.delete(cb)
  }

  /** Спросить актуальный статус. */
  public requestStatus(): void {
    if (isNativeScales()) {
      void nativeScalesStatus()
        .then((status) => this.applyNativeStatus(status))
        .catch(() => undefined)
      return
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ command: "SCALE_GET_STATUS" }))
    }
  }

  /** Текущий статус весов. */
  public getStatus(): ScaleStatus {
    if (isNativeScales()) return { ...this.device }
    return {
      ...this.device,
      connected: this.agentConnected && this.device.connected
    }
  }

  private notifyStatus(): void {
    const status = this.getStatus()
    for (const listener of this.statusListeners) listener(status)
  }
}

export const scalesService = new ScalesService()
